import React, { useMemo } from 'react';
import { Box, Grid, Typography, Button, Card, CardContent } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { colors } from './Theme/WebTheme';
import { FONT_FAMILY } from '../common/sharedFunctions';
import MobileSlider from './MobileSlider';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import RouteIcon from '@mui/icons-material/Route';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import StorefrontIcon from '@mui/icons-material/Storefront';
import { EAT_GREEN } from '../common/eatBrand';

const ServicesSection = React.memo(() => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();

  const langCode = useMemo(() => 
    (i18n?.language ? i18n.language.slice(0, 2).toLowerCase() : 'fr'),
    [i18n?.language]
  );

  const servicesContent = useMemo(() => ({
    fr: {
      title: 'VTC, food et réservation par WhatsApp',
      subtitle: 'Allô Driver réunit les trajets VTC, la livraison de plats et la réservation via WhatsApp avec un suivi clair jusqu’à l’arrivée.',
      services: [
        {
          icon: RestaurantIcon,
          title: 'Allô Driver Eats',
          desc: 'Commandez un plat auprès des restaurants partenaires, suivez le livreur en direct et retrouvez vos reçus dans l’application.',
          action: 'Télécharger l’app',
          eat: true
        },
        {
          icon: StorefrontIcon,
          title: 'Livraison locale',
          desc: 'Une offre pensée pour les restaurateurs et les clients : commandes visibles, livreurs suivis, documents et paiements structurés.',
          action: 'Espace restaurateur',
          path: '/restaurants'
        },
        {
          icon: RouteIcon,
          title: 'Trajets & réservation',
          desc: 'Courses immédiates ou planifiées, trajets urbains, professionnels ou longues distances selon vos besoins.',
          action: 'Réserver par WhatsApp',
          path: '/reservation'
        },
        {
          icon: FlightTakeoffIcon,
          title: 'Aéroports & gares',
          desc: 'Transferts vers Toulouse-Blagnac, gares et stations, avec prise en charge organisée et informations de course.',
          action: 'En savoir plus'
        }
      ]
    },
    en: {
      title: 'VTC, food and WhatsApp booking',
      subtitle: 'Allô Driver brings together VTC rides, food delivery and WhatsApp booking with clear tracking until arrival.',
      services: [
        {
          icon: RestaurantIcon,
          title: 'Allô Driver Eats',
          desc: 'Order a dish from partner restaurants, track the courier live and find receipts in the app.',
          action: 'Get the app',
          eat: true
        },
        {
          icon: StorefrontIcon,
          title: 'Local Delivery',
          desc: 'A flow built for restaurants and customers: visible orders, tracked couriers, structured documents and payments.',
          action: 'Restaurant portal',
          path: '/restaurants'
        },
        {
          icon: RouteIcon,
          title: 'Rides & booking',
          desc: 'Immediate or scheduled rides, urban, professional or long-distance trips depending on your needs.',
          action: 'Book via WhatsApp',
          path: '/reservation'
        },
        {
          icon: FlightTakeoffIcon,
          title: 'Airports & stations',
          desc: 'Transfers to Toulouse-Blagnac, train stations and stops, with organized pickup and ride details.',
          action: 'Learn more'
        }
      ]
    }
  }), []);

  const content = useMemo(() => 
    servicesContent[langCode] || servicesContent.fr,
    [servicesContent, langCode]
  );

  const handleServiceClick = (service) => {
    if (service.eat) {
      const appLinks = document.getElementById('app-download');
      if (appLinks) appLinks.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    navigate(service.path || '/about-us');
  };

  return (
    <Box sx={{ 
      py: { xs: 6, md: 10 }, 
      px: { xs: 2, md: 4 },
      backgroundColor: 'transparent',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Background subtle pattern */}
      <Box sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        opacity: 0.03,
        backgroundImage: 'radial-gradient(circle, #d4af37 1px, transparent 1px)',
        backgroundSize: '50px 50px',
        pointerEvents: 'none'
      }} />

      <Box sx={{ maxWidth: '1200px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography 
            variant="h2" 
            sx={{ 
              fontFamily: FONT_FAMILY,
              fontWeight: 700,
              fontSize: { xs: '32px', md: '48px' },
              color: '#fff',
              mb: 2
            }}
          >
            {content.title}
          </Typography>
          <Typography 
            sx={{ 
              fontFamily: FONT_FAMILY,
              fontSize: { xs: '16px', md: '18px' },
              color: 'rgba(255,255,255,0.7)',
              maxWidth: '800px',
              margin: '0 auto',
              lineHeight: 1.6
            }}
          >
            {content.subtitle}
          </Typography>
        </Box>

        {/* Services Grid */}
        <Grid container spacing={3} sx={{ display: { xs: 'none', sm: 'flex' } }}>
          {content.services.map((service, index) => {
            const IconComponent = service.icon;
            return (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card 
                  sx={{ 
                    height: '100%',
                    backgroundColor: 'rgba(255,255,255,0.05)',
                    backdropFilter: 'blur(10px)',
                    border: `1px solid ${service.eat ? 'rgba(212, 175, 55, 0.45)' : 'rgba(212, 175, 55, 0.2)'}`,
                    borderRadius: '16px',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      borderColor: service.eat ? EAT_GREEN : colors.Button_Primary,
                      boxShadow: service.eat ? '0 12px 40px rgba(212, 175, 55, 0.18)' : `0 12px 40px rgba(212, 175, 55, 0.3)`,
                      backgroundColor: service.eat ? 'rgba(212, 175, 55, 0.08)' : 'rgba(212, 175, 55, 0.08)'
                    }
                  }}
                  onClick={() => handleServiceClick(service)}
                >
                  <CardContent sx={{ p: 3, textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ 
                      width: 64, 
                      height: 64, 
                      borderRadius: '50%',
                      backgroundColor: service.eat ? 'rgba(212, 175, 55, 0.14)' : 'rgba(212, 175, 55, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 20px',
                      border: `2px solid ${service.eat ? EAT_GREEN : colors.Button_Primary}`
                    }}>
                      <IconComponent sx={{ fontSize: 32, color: service.eat ? EAT_GREEN : colors.Button_Primary }} />
                    </Box>
                    
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontFamily: FONT_FAMILY,
                        fontWeight: 700,
                        color: '#fff',
                        mb: 2,
                        fontSize: '20px'
                      }}
                    >
                      {service.title}
                    </Typography>
                    
                    <Typography 
                      sx={{ 
                        fontFamily: FONT_FAMILY,
                        color: 'rgba(255,255,255,0.7)',
                        fontSize: '15px',
                        lineHeight: 1.6,
                        mb: 3,
                        flex: 1
                      }}
                    >
                      {service.desc}
                    </Typography>
                    
                    <Button
                      variant="outlined"
                      sx={{
                        borderColor: service.eat ? EAT_GREEN : colors.Button_Primary,
                        color: service.eat ? EAT_GREEN : colors.Button_Primary,
                        fontFamily: FONT_FAMILY,
                        fontWeight: 600,
                        textTransform: 'none',
                        borderRadius: '8px',
                        py: 1,
                        '&:hover': {
                          borderColor: service.eat ? EAT_GREEN : colors.Button_Primary,
                          backgroundColor: service.eat ? EAT_GREEN : colors.Button_Primary,
                          color: '#000'
                        }
                      }}
                    >
                      {service.action}
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        {/* Mobile Slider */}
        <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
          <MobileSlider spacing={3}>
            {content.services.map((service, index) => {
              const IconComponent = service.icon;
              return (
                <Card 
                  key={index}
                  sx={{ 
                    height: '100%',
                    backgroundColor: 'rgba(255,255,255,0.05)',
                    backdropFilter: 'blur(10px)',
                    border: `1px solid ${service.eat ? 'rgba(212, 175, 55, 0.45)' : 'rgba(212, 175, 55, 0.2)'}`,
                    borderRadius: '16px',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                    '&:active': {
                      transform: 'scale(0.98)',
                      borderColor: service.eat ? EAT_GREEN : colors.Button_Primary,
                      backgroundColor: service.eat ? 'rgba(212, 175, 55, 0.08)' : 'rgba(212, 175, 55, 0.08)'
                    }
                  }}
                  onClick={() => handleServiceClick(service)}
                >
                  <CardContent sx={{ p: 3, textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ 
                      width: 64, 
                      height: 64, 
                      borderRadius: '50%',
                      backgroundColor: service.eat ? 'rgba(212, 175, 55, 0.14)' : 'rgba(212, 175, 55, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 20px',
                      border: `2px solid ${service.eat ? EAT_GREEN : colors.Button_Primary}`
                    }}>
                      <IconComponent sx={{ fontSize: 32, color: service.eat ? EAT_GREEN : colors.Button_Primary }} />
                    </Box>
                    
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontFamily: FONT_FAMILY,
                        fontWeight: 700,
                        color: '#fff',
                        mb: 2,
                        fontSize: '20px'
                      }}
                    >
                      {service.title}
                    </Typography>
                    
                    <Typography 
                      sx={{ 
                        fontFamily: FONT_FAMILY,
                        color: 'rgba(255,255,255,0.7)',
                        fontSize: '15px',
                        lineHeight: 1.6,
                        mb: 3,
                        flex: 1
                      }}
                    >
                      {service.desc}
                    </Typography>
                    
                    <Button
                      variant="outlined"
                      sx={{
                        borderColor: colors.Button_Primary,
                        color: colors.Button_Primary,
                        fontFamily: FONT_FAMILY,
                        fontWeight: 600,
                        textTransform: 'none',
                        borderRadius: '8px',
                        py: 1,
                        '&:hover': {
                          borderColor: colors.Button_Primary,
                          backgroundColor: colors.Button_Primary,
                          color: '#000'
                        }
                      }}
                    >
                      {service.action}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </MobileSlider>
        </Box>
      </Box>
    </Box>
  );
});

export default ServicesSection;
